<template>
  <container-slot>
    <v-col cols="12">
      <h1 class="display-1 font-weight-medium">Add New Article</h1>
    </v-col>
    <v-col cols="12" v-show="successSubmit">
      <v-alert
        :type="!error ? `success` : info ? `info` : `error`"
        ref="alert"
        dismissible
        @input="close"
        class="noMarg"
        v-show="successSubmit"
      >
        {{ msg }}
      </v-alert>
    </v-col>
    <v-col>
      <v-form ref="form" v-model="valid" @submit="submitForm">
        <v-col cols="12" md="6">
          <v-text-field
            prepend-icon="mdi-text-subject"
            name="title"
            label="Article Title"
            type="text"
            v-model="articleTitle"
            ref="userNameInput"
            :rules="articleTitleRules"
            required
          >
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            ref="inputImage"
            label="Article Header Image"
            prepend-icon="mdi-file-image"
            v-model="image"
            :rules="imageRules"
            @change="transformImage"
          >
          </v-file-input>
          <div style="max-width: 300px;" v-if="computedImage" class="mb-2">
            <img style="max-width: 300px;" :src="computedImage" />
          </div>
        </v-col>
        <v-col cols="12">
          <c-k-editor ref="ckeditor" v-model="articleContent" />
        </v-col>
        <v-col cols="12">
          <v-btn
            @click="submitForm"
            color="primary"
            :disabled="!valid || !articleContent"
            :loading="loading"
            >Submit</v-btn
          >
        </v-col>
      </v-form>
    </v-col>
  </container-slot>
</template>

<script>
import ContainerSlot from "@/components/ContainerSlot";
import CKEditorLoading from "@/components/CKEditorLoading";
import LZString from "lz-string";
import axios from "axios";
import moment from "moment";
import cheerio from "cheerio";

import { editNews } from "@/utils/graphqlMutation";
import { getNewsById, news } from "@/utils/graphqlQuery";
import {
  SERVER_ENDPOINT,
  UPLOAD_IMAGE_ENDPOINT,
  NEWS_THUMBNAIL_IMAGE_PATH,
  CKEDITOR_DELETE_ENDPOINT,
  TRANSFORM_REQUEST,
} from "@/utils/constant";

export default {
  apollo: {
    currentNews: {
      fetchPolicy: "network-only",
      query: getNewsById,
      update: function (data) {
        if (!data.getNewsById) {
          this.$router.replace({ path: "/news/edit" });
        } else {
          let ret = null;
          const { title, content, image } = data.getNewsById;
          try {
            const decodedContent = LZString.decompressFromEncodedURIComponent(
              content
            );
            this.articleTitle = title;
            this.articleContent = decodedContent;
            this.notChangedArticleContent = decodedContent;
            if (image) {
              this.computedImage =
                SERVER_ENDPOINT + NEWS_THUMBNAIL_IMAGE_PATH + image;
            }
            ret = data.getNewsById;
          } catch (error) {
            this.error = true;
            this.msg = error.message;
            this.successSubmit = true;
          }
          return ret;
        }
      },
      variables() {
        return {
          newsId: this.newsId,
        };
      },
    },
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventNavigation);
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.preventNavigation);
  },
  async beforeRouteLeave(to, from, next) {
    if (this.articleContent != this.notChangedArticleContent) {
      const permission = confirm("Your work is not saved, continue?");
      if (permission) {
        try {
          this.$emit("willExitPage", true);
          this.saveImagesName();

          const { success, msg } = await this.handleDeleteUploadedImages();
          if (!success) {
            throw new Error(msg);
          } else {
            this.$emit("willExitPage", false);
            next();
          }
        } catch (err) {
          this.$emit("willExitPage", false);
          this.error = true;
          this.msg = err.message;
        }
      }
    } else {
      next();
    }
  },
  components: {
    ContainerSlot,
    CKEditor: () => ({
      component: import("@/components/CKEditor"),
      loading: CKEditorLoading,
    }),
  },
  computed: {},
  data() {
    return {
      articleTitle: "",
      articleTitleRules: [(v) => !!v || "Title can't empty"],
      articleContent: ``,
      computedImage: null,
      currentNews: {},
      error: false,
      image: null,
      imageRules: [
        (v) =>
          typeof v !== "undefined" ||
          !v ||
          v.size < 200000 ||
          "Image size should be less than 200kb!",
        (v) =>
          typeof v !== "undefined" ||
          !v ||
          v.type.includes("image") ||
          "File must be an image",
      ],
      info: false,
      loading: false,
      newsId: this.$route.params.newsId,
      notChangedArticleContent: ``,
      msg: "",
      successSubmit: false,
      uploadedImages: "uploadedImages",
      valid: false,
    };
  },
  methods: {
    close() {
      this.successSubmit = false;
    },
    getDifferentImagesInContent() {
      const $ac = cheerio.load(this.articleContent);
      const $ncac = cheerio.load(this.notChangedArticleContent);
      const imgAc = $ac("img"),
        imgNcac = $ncac("img");

      let imgAcNames = [],
        imgNcacNames = [];

      const folderAndFileName = (imgSrc) => {
        const srcSpl = imgSrc.split("/");
        const len = srcSpl.length;
        return srcSpl[len - 2] + "/" + srcSpl[len - 1];
      };

      if (imgAc.length) {
        imgAc.each(function (i, elem) {
          imgAcNames.push(folderAndFileName($ac(this).attr("src")));
        });
      }
      if (imgNcac.length) {
        imgNcac.each(function (i, elem) {
          imgNcacNames.push(folderAndFileName($ncac(this).attr("src")));
        });
      }

      return imgAcNames.filter((imgName) => !imgNcacNames.includes(imgName));
    },
    getImagesName() {
      return JSON.parse(localStorage.getItem(this.uploadedImages));
    },
    async handleDeleteUploadedImages() {
      const images = this.getImagesName();
      if (images && images.length) {
        try {
          const { status, msg } = await this.xhrDeleteImage(images);
          return {
            success: status,
            msg,
          };
        } catch (err) {
          return {
            success: false,
            msg: err.message,
          };
        }
      } else {
        return {
          success: true,
          msg: "No images need to be deleted",
        };
      }
    },
    preventNavigation(e) {
      if (this.articleContent == this.notChangedArticleContent) {
        return;
      }
      this.saveImagesName();
      e.preventDefault();
      e.returnValue = "";
    },
    saveImagesName() {
      const diff = this.getDifferentImagesInContent();
      localStorage.setItem(this.uploadedImages, JSON.stringify(diff));
    },
    async submitForm(e) {
      e.preventDefault();
      this.successSubmit = false;
      let filename = "";
      if (this.valid) {
        this.loading = true;
        try {
          // Handle Header Image Upload
          if (this.image) {
            const formData = new FormData();
            formData.append("file_image", this.image);
            const { data: uploadResponse } = await axios.post(
              SERVER_ENDPOINT + UPLOAD_IMAGE_ENDPOINT,
              formData
            );
            if (!uploadResponse.success) {
              throw new Error("Error while uploading image");
            }
            filename = uploadResponse.imagePath;
          } else {
            filename = this.currentNews.image;
          }
          // End Handle Header Image Upload

          // Main function
          try {
            const encodedArticleContent = LZString.compressToEncodedURIComponent(
              this.articleContent
            );

            const newsObj = {
              id: this.newsId,
              title: this.articleTitle,
              content: encodedArticleContent,
              image: filename,
            };

            const gqlNews = news;
            const that = this;

            const { data } = await this.$apollo.mutate({
              mutation: editNews,
              variables: {
                newsObj,
              },
              update: async (store, { data: { editNews } }) => {
                try {
                  if (editNews.success == 1) {
                    const allNews = store.readQuery({
                      query: news,
                    });
                    const updatedAllNews = allNews.news.map((n) =>
                      n.id == that.currentNews.id
                        ? { ...n, ...that.currentNews, ...newsObj }
                        : n
                    );
                    store.writeQuery({
                      query: gqlNews,
                      data: {
                        news: updatedAllNews,
                      },
                    });
                  }
                  return editNews;
                } catch (error) {
                  console.log(error.message);
                  return null;
                }
              },
            });

            if (data.editNews) {
              const { success, msg } = data.editNews;
              if (success == 0) {
                throw new Error(msg);
              } else if (success == -1) {
                this.error = true;
                this.info = true;
                this.msg = msg;
              } else {
                this.error = false;
                this.msg = msg;
              }
            } else {
              this.error = true;
              this.msg = `Something went wrong`;
            }
          } catch (error) {
            throw new Error(error.message);
          }
        } catch (error) {
          this.error = true;
          this.msg = error.message;
        } finally {
          this.successSubmit = true;
          this.loading = false;
        }
      }
    },
    transformImage() {
      if (this.image) {
        if (!this.image.type.includes("image")) return null;
        const reader = new FileReader();
        reader.readAsDataURL(this.image);
        reader.onload = () => {
          this.computedImage = reader.result;
        };
      }
      this.computedImage = null;
    },
    async xhrDeleteImage(images) {
      const { data } = await axios.post(
        `${SERVER_ENDPOINT}${CKEDITOR_DELETE_ENDPOINT}`,
        {
          images,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          transformRequest: (jsonData) => TRANSFORM_REQUEST(jsonData),
        }
      );
      const { status, msg } = data;
      if (status) {
        localStorage.removeItem(this.uploadedImages);
      }
      return data;
    },
  },
  async mounted() {
    this.$emit("willExitPage", true);
    try {
      const { success, msg } = await this.handleDeleteUploadedImages();
      if (!success) {
        throw new Error(err.message);
      }
    } catch (err) {
      this.error = true;
      this.msg = err.message;
    }
    this.$emit("willExitPage", false);
  },
  name: "EditNews",
  title() {
    return "Edit News";
  },
};
</script>

<style scoped>
.noMarg {
  margin: 0;
}
</style>
